<template>
  <form
    class="mx-auto flex max-w-lg rounded-2xl bg-white p-4 outline-2 outline-black/10"
    @submit.prevent
  >
    <InputSchoolSearch
      class="min-w-0 grow"
      :initial-query="props.initialQuery"
      name="school"
      placeholder="Find your school"
      :sa-only="props.saOnly"
    />
  </form>
</template>

<script setup lang="ts">
import { object, nullable, number, string } from 'zod'
import { getSaSchoolPath, getSchoolPath } from 'shared'

import type { School } from '#payload-types'

// Provide a vee-validate form context so the InputSchoolSearch field binds correctly.
const validationSchema = toTypedSchema(
  object({
    school: nullable(
      object({
        id: number(),
        slug: string(),
      }),
    ),
  }),
)

interface HomeSchoolSearchProps {
  navigateOnSelect?: boolean
  /**
   * Opens the box on a school name the visitor has already named — set by /school-stats when
   * someone lands there from a slug that several schools once shared.
   */
  initialQuery?: string
  /** Searches only South Australian schools and links into /sa, for the SA landing page. */
  saOnly?: boolean
}

const props = withDefaults(defineProps<HomeSchoolSearchProps>(), {
  navigateOnSelect: true,
})

const emit = defineEmits<{ select: [school: School] }>()

useForm({ validationSchema })

// Picking a school from the autocomplete is the search — take them straight there.
const { value: school } = useField<School | null>('school')

watch(school, (selected) => {
  if (!selected) return

  emit('select', selected)

  if (props.navigateOnSelect && selected.slug) {
    navigateTo(
      props.saOnly ? getSaSchoolPath(selected) : getSchoolPath(selected),
    )
  }
})
</script>
